import {VanillaReaderUiDialog} from './VanillaReaderUiDialog';
import {ITreeViewItemSourceData, TreeView} from "../utils/TreeView";
import {VanillaUiDialogResultCallback} from "../VanillaReaderUI/VanillaReaderUiModel";
import {VanillaStringValueCallback} from "../VanillaReader/VanillaReaderModel";
import {VanillaReaderLandmark} from "../VanillaReader/VanillaReaderLandmark";
import {
    ContainerContentBlockType,
    MediaContentBlockType,
    TextContentBlockType
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";

/**
 * # VanillaReaderUiDialogActions
 *
 * ## RESPONSIBILITIES
 *
 * ## RELATED TYPES
 * - VanillaReaderUiDialogActionsController
 *
 */

export class VanillaReaderUiDialogPublicationLandmarks extends VanillaReaderUiDialog<void> {
    private _headingsListElement: HTMLElement;
    private _headingsItemClickedCallback: VanillaStringValueCallback | undefined = undefined;
    private _treeViewLandmarks: TreeView | undefined = undefined;
    private _treeViewLandmarkRootItems: ITreeViewItemSourceData[];
    private _hasBeenPopulated: boolean = false;

    private _bodyStyle = `
        <style>
            
            #vanilla-reader__dialog-landmarks__list ul[role=tree] {
                padding-left: 0;
                list-style: none;
            }
                        
            #vanilla-reader__dialog-landmarks__list ul[role=tree] button {
                font-size: x-large;
                margin-left: 0;
            }

            #vanilla-reader__dialog-landmarks__list ul[role=group] {
                padding-left: 1em;
                list-style: none;
            }
            
            #vanilla-reader__dialog-landmarks__list ul[role=group] li:not([data-treeview-role=group]) {
               margin-left: 1em;
            }
            
            #vanilla-reader__dialog-landmarks__list li {
                align-items: center;
            }

            #vanilla-reader__dialog-landmarks__list li a {
                max-width: 70%;
            }

            #vanilla-reader__dialog-landmarks__list li[aria-selected=true] {
                text-decoration: underline;
            }
            
            #vanilla-reader__dialog-landmarks__list li[aria-expanded=true] ul {
                border-left: 1px dotted var(--vanilla-reader__color__ui__outline-dark);
            }
              
        </style>`;

    private _bodyTemplate = `
            <section aria-label="Landmarks" id="vanilla-reader__dialog-landmarks__list"></section>`;

    constructor(
        _hostElement: HTMLElement,
        _insertPosition: InsertPosition = 'afterbegin',
    ) {
        super(_hostElement, 'vanilla-reader__dialog-landmarks', 'Landmarks', _insertPosition);

        this._dialogBodyElement.innerHTML = this._bodyTemplate;
        this._dialogElement.insertAdjacentHTML('afterbegin', this._bodyStyle);
        this._headingsListElement = this._dialogElement.querySelector<HTMLElement>('#vanilla-reader__dialog-landmarks__list')!;

        this._treeViewLandmarkRootItems = [];
    }

    public get hasBeenPopulated(): boolean {
        return this._hasBeenPopulated;
    }

    show(silent: boolean = false, returnFocusOnClose: boolean = true, onDialogCloseCallbackOverride?: VanillaUiDialogResultCallback<void>, collapseAll: boolean = true): void {
        super.show(silent, returnFocusOnClose, onDialogCloseCallbackOverride);
        if(collapseAll) {
            this._treeViewLandmarks?.collapseAll();
        }
    }

    close(silent: boolean = false, returnDialogResult: boolean = true): void {
        silent = true;
        super.close(silent, returnDialogResult);
    }

    onLandmarkItemClicked(callback: VanillaStringValueCallback): void {
        this._headingsItemClickedCallback = callback;
    }

    populate(landmarks: VanillaReaderLandmark[] = []): void {

        this._treeViewLandmarks?.destroy();

        let headingItemData: ITreeViewItemSourceData<VanillaReaderLandmark | undefined>[];
        let tableItemData: ITreeViewItemSourceData<VanillaReaderLandmark | undefined>[];
        let figureItemData: ITreeViewItemSourceData<VanillaReaderLandmark | undefined>[];
        let listItemData: ITreeViewItemSourceData<VanillaReaderLandmark | undefined>[];

        let filteredLandmarks = landmarks.filter(landmark => (landmark.description && landmark.description !== '') || (landmark.textContent && landmark.textContent !== ''));

        let headingLandmarkData = filteredLandmarks.filter((landmark)=> {
            return (landmark.blockType === TextContentBlockType.HEADING1 ||
            landmark.blockType === TextContentBlockType.HEADING2 ||
            landmark.blockType === TextContentBlockType.HEADING3 ||
            landmark.blockType === TextContentBlockType.HEADING4 ||
            landmark.blockType === TextContentBlockType.HEADING5 ||
            landmark.blockType === TextContentBlockType.HEADING6)
        });
        
        headingItemData = headingLandmarkData.map((landmark)=>{
           return {
               id: landmark.id.toString(),
               data: landmark,
               children: [],
               textContent: `${landmark.textContent} (${landmark.blockType})`
           }
        });

        let tableLandmarkData = filteredLandmarks.filter(landmark => landmark.blockType === ContainerContentBlockType.TABLE);
        tableItemData = tableLandmarkData.map((landmark)=>{
           return {
               id: landmark.id.toString(),
               data: landmark,
               children: [],
               textContent: landmark.textContent || landmark.description || 'Unlabelled table'
           }
        });

        let figureLandmarkData = filteredLandmarks.filter(landmark => landmark.blockType === ContainerContentBlockType.FIGURE || landmark.blockType === MediaContentBlockType.IMAGE);
        figureItemData = figureLandmarkData.map((landmark)=>{
           return {
               id: landmark.id.toString(),
               data: landmark,
               children: [],
               textContent: landmark.description || landmark.description || 'Unlabelled figure'
           }
        });

        let listLandmarkData = filteredLandmarks.filter(landmark => landmark.blockType === (
            ContainerContentBlockType.ORDERED_LIST ||
            ContainerContentBlockType.UNORDERED_LIST ||
            ContainerContentBlockType.DESCRIPTION_LIST))

        listItemData = listLandmarkData.map((landmark)=>{
           return {
               id: landmark.id.toString(),
               data: landmark,
               children: [],
               textContent: landmark.textContent || landmark.description || 'Unlabelled list'
           }
        });

        this._treeViewLandmarkRootItems = [

            {
                id: 'headings',
                data: {},
                children: headingItemData,
                textContent: headingItemData.length > 0 ? 'Headings' : 'No labeled headings found'
            },
            {
                id: 'tables',
                data: {},
                children: tableItemData,
                textContent: tableItemData.length > 0 ? 'Tables' : 'No labeled tables found'
            },
            {
                id: 'figures',
                data: {},
                children: figureItemData,
                textContent: figureItemData.length > 0 ? 'Figures' : 'No labeled figures found'
            },
            {
                id: 'lists',
                data: {},
                children: listItemData,
                textContent: listItemData.length > 0 ? 'Lists' : 'No labeled lists found'
            },
        ]

        this._treeViewLandmarks = new TreeView<VanillaReaderLandmark | undefined>(this._headingsListElement, this._treeViewLandmarkRootItems);
        this._treeViewLandmarks.cssClassName = 'vanilla-reader__dialog-landmarks-list';

        this._treeViewLandmarks.addItemClickEventHandler((landmarkItem: ITreeViewItemSourceData<VanillaReaderLandmark>) => {
            if (this._headingsItemClickedCallback && landmarkItem.data.locatorUrl) {
                this._headingsItemClickedCallback(landmarkItem.data.locatorUrl);
            } else {
                let treeViewItem = this._treeViewLandmarks?.treeViewItemDataToTreeViewItemMap.get(landmarkItem);
                if(treeViewItem) {
                    treeViewItem.setIsExpanded(!treeViewItem.getIsExpanded());
                }
            }
        });

        this._treeViewLandmarks.render();

        this._hasBeenPopulated = true;
    }

    empty(): void {
        if (this._treeViewLandmarks) {
            this._treeViewLandmarks.destroy();
        }
    }

}
